import type { StopItem, StopPhysicalCheck } from '@ctcells/api'
import type { SafetyState } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

/**
 * 시작 창 [정지 풀기]의 흐름(`StopsReleaseFix.tsx`) — 풀고 → 항목별 결과 → 목록에서 빠질 때까지(상한).
 * 실측(히나 2026-10-24 로컬): 원심 `open_door` 로 연 문 → `door_open:centrifuge-1` · 확인 없이 422 `physical_check_required` ·
 * `physical_checks: ['door_area']` 로 200 `done` · 약 3초 뒤 목록 · 점검(`SAFETY_SELFTEST_FAIL`)에서 빠졌다.
 */
/** 풀린 항목이 목록에서 빠지기를 기다리는 상한 · 간격 — 문 닫기는 202 뒤 모듈이 돈다(실측 약 3초) */
export const STOPS_SETTLE_MS = 15_000
export const STOPS_SETTLE_POLL_MS = 2_000

export interface StopsReleasePorts {
  listStops: () => Promise<readonly StopItem[]>
  release: (input: {
    ids: readonly string[]
    reason: string
    physicalChecks: readonly StopPhysicalCheck[]
  }) => Promise<{
    items: readonly { id: string; done: boolean; error_code?: string | null; said: string }[]
  }>
  sleep: (ms: number) => Promise<void>
  now: () => number
}

/**
 * 풀고 → 항목별 결과 확인 → 목록에서 빠질 때까지(상한) 다시 읽는다. 못 풀었으면 사유를 던진다(부르는 `run` 이 그 줄에 적는다).
 */
export async function releaseStopsAndSettle(
  ports: StopsReleasePorts,
  input: { ids: readonly string[]; reason: string; physicalChecks: readonly StopPhysicalCheck[] },
): Promise<void> {
  const result = await ports.release(input)
  const failed = result.items.filter((item) => !item.done)
  if (failed.length > 0)
    throw new Error(
      t('pfStops.notDone', {
        items: failed
          .map(
            (item) => `${item.id} — ${item.said}${item.error_code ? ` (${item.error_code})` : ''}`,
          )
          .join(' · '),
      }),
    )
  const wanted = new Set(input.ids)
  const started = ports.now()
  for (;;) {
    const left = (await ports.listStops()).filter((item) => wanted.has(item.id))
    if (left.length === 0) return
    if (ports.now() - started >= STOPS_SETTLE_MS)
      throw new Error(
        t('pfStops.stillListed', {
          seconds: String(Math.round(STOPS_SETTLE_MS / 1000)),
          items: left.map((item) => item.id).join(', '),
        }),
      )
    await ports.sleep(STOPS_SETTLE_POLL_MS)
  }
}

/**
 * 정지 줄 글 — 🔴 **전체 안전 정지(`kind === 'safety'`) 줄만** 서버 `said` 원문 대신 구조 값으로 짓는다(eric 2026-10-29
 * 「시스템 안전 비상정지 · 원인 화면 단추 — 이게 뭐야?」). 값은 `GET /safety` 의 `state` · `cause`(계약 `SafetyCause.kind` 넷 —
 * 서버 `services/safety.py` `cause_said` 의 갈래와 같다: operator 화면 단추 · hw 모듈 버튼 · fault 모듈 고장 · unknown).
 * - `emergency_stop` 이면 머리 「E-STOP」, 그 밖의 안전 상태면 「안전정지」
 * - hw · fault 인데 모듈을 모르면 서버처럼 「원인 모름」
 * - 🔴 `cause` 를 못 받았으면(조회 전 · 실패 · 정지 아님이라 null) **원문 그대로** — 지어내지 않는다
 * - 다른 종류(문 열림 · 격리 · 로봇 파지 …)는 원문 그대로다
 */
export function stopItemText(
  item: Pick<StopItem, 'kind' | 'said'>,
  safety: Pick<SafetyState, 'state' | 'cause'> | undefined,
): string {
  if (item.kind !== 'safety') return item.said
  const cause = safety?.cause
  if (safety === undefined || cause === null || cause === undefined) return item.said
  const head = t(
    safety.state === 'emergency_stop' ? 'stopsRelease.safety.estop' : 'stopsRelease.safety.stop',
  )
  const module = cause.module_name ?? cause.module_id ?? null
  const text =
    cause.kind === 'operator'
      ? t('stopsRelease.safety.cause.operator')
      : cause.kind === 'hw' && module !== null
        ? t('stopsRelease.safety.cause.hw', { module })
        : cause.kind === 'fault' && module !== null
          ? cause.code
            ? t('stopsRelease.safety.cause.fault', { module, code: cause.code })
            : t('stopsRelease.safety.cause.faultNoCode', { module })
          : t('stopsRelease.safety.cause.unknown')
  return t('stopsRelease.safety.line', { head, cause: text })
}
